/** Destination filter autocomplete component for CDRs page. */
import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
} from "@mui/material";
import { LocationOn as LocationIcon } from "@mui/icons-material";
import { Destination } from "../../types/destination";
import apiClient from "../../api/client";

interface DestinationFilterProps {
  value: string;
  onChange: (destination: string) => void;
  startDate: string;
  endDate: string;
}

/** Debounce hook to delay API calls. */
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/** Destination filter with autocomplete and call count display. */
export default function DestinationFilter({
  value,
  onChange,
  startDate,
  endDate,
}: DestinationFilterProps) {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<Destination[]>([]);
  const [loading, setLoading] = useState(false);

  const debouncedInput = useDebounce(inputValue, 300);

  /** Fetch destinations matching the search input. */
  useEffect(() => {
    const fetchDestinations = async () => {
      if (!debouncedInput || debouncedInput.length < 1) {
        setOptions([]);
        return;
      }

      setLoading(true);
      try {
        const response = await apiClient.get("/api/v1/destinations/popular", {
          params: {
            search: debouncedInput,
            start_date: startDate,
            end_date: endDate,
          },
        });
        setOptions(response.data.destinations || []);
      } catch (error) {
        console.error("[DestinationFilter] Failed to fetch destinations:", error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDestinations();
  }, [debouncedInput, startDate, endDate]);

  /** Handle selection change. */
  const handleChange = (_event: React.SyntheticEvent, newValue: Destination | string | null) => {
    if (typeof newValue === "string") {
      onChange(newValue);
    } else if (newValue?.prefix) {
      onChange(newValue.prefix);
    } else {
      onChange("");
    }
  };

  /** Delete the selected destination filter. */
  const handleDelete = () => {
    onChange("");
  };

  /** Render option with prefix, description, and call count. */
  const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: Destination) => (
    <li {...props}>
      <Box>
        <Typography variant="body1">
          <LocationIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: "middle" }} />
          {option.prefix} - {option.description}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {option.call_count.toLocaleString()} calls
          {option.avg_cost > 0 && ` • Avg: $${option.avg_cost.toFixed(4)}`}
        </Typography>
      </Box>
    </li>
  );

  return (
    <Box sx={{ minWidth: 300 }}>
      {value ? (
        <Chip
          label={`Destination: ${value}`}
          onDelete={handleDelete}
          sx={{ minWidth: 200 }}
        />
      ) : (
        <Autocomplete
          freeSolo
          options={options}
          getOptionLabel={(option) => {
            if (typeof option === "string") {
              return option;
            }
            return option.prefix;
          }}
          filterOptions={(x) => x}
          inputValue={inputValue}
          onInputChange={(_event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          onChange={handleChange}
          renderOption={renderOption}
          loading={loading}
          noOptionsText={inputValue.length < 1 ? "Type to search destinations" : "No destinations found"}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Destination"
              placeholder="Search by prefix or country"
              size="small"
              sx={{ minWidth: 300 }}
            />
          )}
        />
      )}
    </Box>
  );
}
